/**
 * FirstVisitIntro
 * A one-time explainer for people landing on Prompt-Brush for the first time. The hook —
 * "the first non-AI generative art model" — is a great line that takes a beat to decode,
 * so this spells out the loop in three steps: a prompt, a person, the gallery.
 * Shown once (remembered locally), dismissible, and never blocks the page for returning visitors.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const SEEN_KEY = 'pb:intro:v1';
const body = { fontFamily: 'FK Grotesk Mono, monospace' } as const;

const STEPS = [
  { n: '01', title: 'Someone writes a prompt', copy: 'A line of text. “A cat astronaut floating past the moon.”' },
  { n: '02', title: 'A person draws it by hand', copy: 'Black ink on paper. No filters, no generators — just a human deciding what it looks like.' },
  { n: '03', title: 'It joins the gallery', copy: 'Every prompt is drawn once, so each drawing is somebody’s single answer to it.' },
];

export function FirstVisitIntro() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Never interrupt someone mid-drawing, and never get in an admin's way.
  const suppressed = pathname.startsWith('/admin') || pathname === '/user-draw';

  useEffect(() => {
    if (suppressed) return;
    try {
      if (!localStorage.getItem(SEEN_KEY)) setOpen(true);
    } catch {
      // private mode / storage blocked — just don't show it
    }
  }, [suppressed]);

  const close = () => {
    try {
      localStorage.setItem(SEEN_KEY, '1');
    } catch {
      // ignore
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/60 p-4"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label="What is Prompt-Brush?"
    >
      <div
        className="relative w-full max-w-lg rounded-xl bg-background border border-border p-7 md:p-9"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-4 text-2xl leading-none text-muted-foreground hover:text-foreground transition-colors"
        >
          ×
        </button>

        <p className="text-[11px] uppercase tracking-[0.2em] mb-7" style={{ ...body, color: 'hsl(var(--pb-mark))' }}>
          The first non-AI generative art model
        </p>

        <ol className="space-y-5 mb-8 list-none p-0">
          {STEPS.map((s) => (
            <li key={s.n} className="flex gap-4">
              <span className="text-[11px] pt-1 shrink-0" style={{ ...body, color: 'hsl(var(--pb-mark))' }}>
                {s.n}
              </span>
              <span>
                <span className="block text-[15px] text-foreground" style={body}>
                  {s.title}
                </span>
                <span className="block text-[13px] leading-relaxed text-muted-foreground mt-1" style={body}>
                  {s.copy}
                </span>
              </span>
            </li>
          ))}
        </ol>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => {
              close();
              navigate('/prompts');
            }}
            className="rounded-full bg-foreground text-background px-5 py-2.5 text-[12px] tracking-wider hover:opacity-90 transition-opacity"
            style={body}
          >
            DRAW A PROMPT
          </button>
          <button
            onClick={close}
            className="text-[12px] text-muted-foreground hover:text-foreground transition-colors"
            style={body}
          >
            Just looking
          </button>
        </div>
      </div>
    </div>
  );
}

export default FirstVisitIntro;
